import {
  PageType,
  type ReportPageConfig,
} from '../components/PageConfigProvider/types';
import { FilterId } from '../filters/types/filters';
import {
  buildFilterGroups,
  encodeFilterGroups,
} from '../filters/utils/filterGroupsUtils';
import { buildReportPages } from './report-page-builder';

/** Preloads primary company + peers when opening the sample via ReportSampleLink (?filterGroups=...). */
const orgHealthSampleFilterGroups = encodeFilterGroups(
  buildFilterGroups(
    [
      {
        groupKey: '36eca886-b4ae-477d-8f8d-371106af8556',
        filterIds: [FilterId.COMPANY],
        defaultSelections: {
          [FilterId.COMPANY]: [
            { id: '8_256_1116644', name: 'Workday', levelId: 'company' },
          ],
        },
      },
      {
        groupKey: 'a7ba0b5e-dcd5-45d4-a83c-9aa764d74883',
        filterIds: [FilterId.COMPANY],
        defaultSelections: {
          [FilterId.COMPANY]: [
            { id: '8_257_919809', name: 'Snowflake', levelId: 'company' },
            { id: '8_257_1005273', name: 'MongoDB', levelId: 'company' },
            { id: '8_248_1010813', name: 'Palantir', levelId: 'company' },
            {
              id: '8_256_256277',
              name: 'Slack',
              levelId: 'company',
            },
          ],
        },
      },
    ],
    { primaryFilterId: FilterId.COMPANY }
  )
);

const companyParam = {
  parameterId: '36eca886-b4ae-477d-8f8d-371106af8556',
  grouped: false,
};
const competitorsGrouped = {
  parameterId: 'a7ba0b5e-dcd5-45d4-a83c-9aa764d74883',
  grouped: true,
};
const competitorsUngrouped = {
  parameterId: 'a7ba0b5e-dcd5-45d4-a83c-9aa764d74883',
  grouped: false,
};

const companyAndCompetitorsGrouped = [companyParam, competitorsGrouped];
const bothParamsUngrouped = [companyParam, competitorsUngrouped];

export const reportOrgHealth: ReportPageConfig = (() => {
  const { items, grid } = buildReportPages('org-health', [
    [
      {
        id: 'b294aedb-29ee-47be-aaa7-2e34e2869e3e',
        title: '',
        position: { height: 32 },
        plot: {
          chartType: 'ai-summary',
          plotParameters: [companyParam],
        },
      },
    ],
    [
      {
        id: 'a8f1cb31-dd92-49c2-867b-e4f1eff0510d',
        title: '',
        position: { height: 9 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Workforce Composition\n\n{{PARAM "Company"}} exhibits a {{FORMAT (VALUE "attrition_rate") format="%"}} attrition rate, on par with its peers, with a {{FORMAT (VALUE "hiring_rate") format="%"}} hiring rate and a {{FORMAT (VALUE "tenure") format="yr"}} tenure, indicating a higher-velocity talent model.\n\nTheir {{FORMAT (VALUE "growth_rate") format="%"}} growth rate confirms the high turnover as expansion-driven rather than pure replacement turnover, consistent with growth-stage companies in competitive talent markets.',
          plotParameters: [],
        },
      },
      {
        id: 'c375409d-baca-41ac-a131-bc22acefbe42',
        title: 'Headcount',
        position: { y: 9, width: 16, height: 8 },
        plot: {
          chartType: 'line',
          defaultMetricOptions: ['headcount'],
          plotParameters: companyAndCompetitorsGrouped,
        },
      },
      {
        id: 'a7bae3c9-d0ea-4c1b-ba91-e637b92e45a8',
        title: 'Hiring Rate',
        position: { x: 16, y: 9, width: 16, height: 8 },
        plot: {
          chartType: 'line',
          defaultMetricOptions: ['hiring_rate'],
          plotParameters: companyAndCompetitorsGrouped,
        },
      },
      {
        id: '0550c364-eaae-4fd7-8716-9e794f5a6d77',
        title: 'Attrition Rate',
        position: { x: 32, y: 9, width: 16, height: 8 },
        plot: {
          chartType: 'line',
          defaultMetricOptions: ['attrition_rate'],
          plotParameters: companyAndCompetitorsGrouped,
        },
      },
      {
        id: '27d45153-5eb8-4cfb-9f2f-04b1516a578c',
        title: 'Growth Rate',
        position: { y: 17, width: 16, height: 8 },
        plot: {
          chartType: 'line',
          defaultMetricOptions: ['growth_rate'],
          plotParameters: companyAndCompetitorsGrouped,
        },
      },
      {
        id: '763773e0-ac71-4c72-8dd0-5912c2947c23',
        title: 'Tenure',
        position: { x: 16, y: 17, width: 16, height: 8 },
        plot: {
          chartType: 'line',
          defaultMetricOptions: ['tenure'],
          plotParameters: companyAndCompetitorsGrouped,
        },
      },
      {
        id: '42145075-67d9-46f4-bff0-22f61ca1c479',
        title: 'Salary',
        position: { x: 32, y: 17, width: 16, height: 8 },
        plot: {
          chartType: 'line',
          defaultMetricOptions: ['salary'],
          plotParameters: companyAndCompetitorsGrouped,
        },
      },
    ],
    [
      {
        id: 'd2a17fde-59f5-4696-b275-6454e822293e',
        title: '',
        position: { height: 8 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Sentiment Analysis\n\n{{PARAM "Company"}} maintains a {{FORMAT (divide (subtract (VALUE "salary" (PARAM "Company")) (AVG "salary" excludeEntity=(PARAM "Company"))) (AVG "salary" excludeEntity=(PARAM "Company"))) format="%"}} discount over peer average salary. \n\n- Employee sentiment data still shows net positive compensation satisfaction, with compensation sentiment of {{FORMAT (VALUE "compensation" entity=(PARAM "Company")) format="%"}}\n\n- Overall sentiment of {{FORMAT (VALUE "overall" entity=(PARAM "Company")) format="%"}} places {{PARAM "Company"}} in line with peers {{FORMAT (VALUE "overall" excludeEntity=(PARAM "Company")) format="%"}}\n\n- Work-life balance lags peer averages with a value of {{FORMAT (VALUE "work_life_balance" entity=(PARAM "Company")) format="%"}} vs {{FORMAT (VALUE "work_life_balance" excludeEntity=(PARAM "Company")) format="%"}}',
          plotParameters: companyAndCompetitorsGrouped,
        },
      },
      {
        id: '629a5b0b-b3ea-420a-882b-06b5388d3ca1',
        title: 'Sentiment Drivers',
        isTitleManuallySet: true,
        position: { y: 8, width: 21, height: 12 },
        plot: {
          chartType: 'diverging-bar',
          defaultMetricOptions: ['sentiment_top_bottom'],
          divergingBar: {
            negativeLabel: '',
            positiveLabel: '',
          },
          plotParameters: [companyParam],
        },
      },
      {
        id: '39125afe-b4be-4a40-93c3-6dc70aa2e610',
        title: 'Overall',
        position: { x: 21, y: 8, width: 14, height: 6 },
        plot: {
          chartType: 'bar',
          sort: 'DESC',
          defaultMetricOptions: ['overall'],
          plotParameters: bothParamsUngrouped,
        },
      },
      {
        id: '021de8f8-b5e5-4af1-8dd3-cbc82af4e9aa',
        title: 'Compensation',
        position: { x: 35, y: 8, width: 13, height: 6 },
        plot: {
          chartType: 'bar',
          sort: 'DESC',
          defaultMetricOptions: ['compensation'],
          plotParameters: bothParamsUngrouped,
        },
      },
      {
        id: '2680d6b5-0fcf-48fe-877a-dae139b8116e',
        title: 'Business Outlook',
        position: { x: 21, y: 14, width: 14, height: 6 },
        plot: {
          chartType: 'bar',
          sort: 'DESC',
          defaultMetricOptions: ['business_outlook'],
          plotParameters: bothParamsUngrouped,
        },
      },
      {
        id: 'd6af4239-f3b7-41dc-88cb-26a652c3718d',
        title: 'Work-Life Balance',
        position: { x: 35, y: 14, width: 13, height: 6 },
        plot: {
          chartType: 'bar',
          sort: 'DESC',
          defaultMetricOptions: ['work_life_balance'],
          plotParameters: bothParamsUngrouped,
        },
      },
      {
        id: '2c29f81d-42a8-47d9-a93b-d5a61ebe5783',
        title: '',
        position: { y: 20, height: 12 },
        plot: {
          chartType: 'text',
          textHtml:
            '#### Positive Summaries\n\n{{VALUE "positive_review" (PARAM "Company")}}\n\n#### Negative Summaries\n\n{{VALUE "negative_review" (PARAM "Company")}}',
          plotParameters: [companyParam],
        },
      },
    ],
    [
      {
        id: 'ebd216f7-67b0-4316-b33c-6247965d4d5e',
        title: '',
        position: { height: 7 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Compensation Analysis\n\n{{PARAM "Company"}} has an average salary of {{FORMAT (VALUE "base_salary" entity=(PARAM "Company")) format="$"}} compared to a peer value of {{FORMAT (VALUE "base_salary" excludeEntity=(PARAM "Company")) format="$"}}, and a total compensation of {{FORMAT (VALUE "total_compensation" entity=(PARAM "Company")) format="$"}} vs peers\' {{FORMAT (VALUE "total_compensation" excludeEntity=(PARAM "Company")) format="$"}}.\n',
          plotParameters: companyAndCompetitorsGrouped,
        },
      },
      {
        id: 'f9719126-aa10-450c-8365-dc8f6af61d2d',
        title: 'Base Salary',
        position: { y: 7, width: 26, height: 8 },
        plot: {
          chartType: 'bar',
          sort: 'DESC',
          defaultMetricOptions: ['base_salary'],
          plotParameters: bothParamsUngrouped,
        },
      },
      {
        id: '865227e7-ce10-425d-adf7-15afc7d546a2',
        title: 'Base Salary by Role',
        position: { x: 27, y: 7, width: 21, height: 12 },
        plot: {
          chartType: 'box',
          secondaryDimension: 'role',
          defaultMetricOptions: ['base_salary'],
          plotParameters: [companyParam],
        },
      },
      {
        id: '74f7e4a6-5580-4e9e-9dfd-508dfff8e341',
        title: 'Total Compensation',
        position: { y: 15, width: 26, height: 8 },
        plot: {
          chartType: 'bar',
          sort: 'DESC',
          defaultMetricOptions: ['total_compensation'],
          plotParameters: bothParamsUngrouped,
        },
      },
      {
        id: 'aafd75bc-ee3f-4a68-aa9e-94650956e0f9',
        title: 'Base Salary by Geography',
        position: { x: 27, y: 19, width: 21, height: 13 },
        plot: {
          chartType: 'box',
          secondaryDimension: 'geography',
          defaultMetricOptions: ['base_salary'],
          plotParameters: [companyParam],
        },
      },
      {
        id: 'a7375e06-384e-409c-90f1-2603fc50192c',
        title: 'Total Spend (Salary) by Role',
        position: { y: 23, width: 27, height: 9 },
        plot: {
          chartType: 'treemap',
          secondaryDimension: 'role',
          groupBySegment: false,
          defaultMetricOptions: ['total_spend_salary'],
          plotParameters: [companyParam],
        },
      },
    ],
    [
      {
        id: '9247ae11-078c-46a5-8a89-c6ededef83a6',
        title: '',
        position: { width: 26, height: 16 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Hiring Activity Signal\n\n{{PARAM "Company"}} job postings declined from peak of {{MAX_POINT "postings_active" (PARAM "Company")}} in 2023 to {{VALUE "postings_active" (PARAM "Company")}}\nin 2026, a {{FORMAT (divide (subtract (MAX_POINT "postings_active" (PARAM "Company")) (VALUE "postings_active" (PARAM "Company"))) (MAX_POINT "postings_active" (PARAM "Company"))) format="%"}} reduction. This deceleration aligns with broader tech sector normalization following pandemic-era expansion. \n\nBusiness and Technology remains the largest hiring category, consistent with continued product development investment. The moderated hiring pace suggests operational discipline while maintaining technical capability development.',
          plotParameters: [companyParam],
        },
      },
      {
        id: '3cc75d4c-55a2-49c6-98ce-05be3d5524db',
        title: 'Postings Active by Role',
        position: { x: 26, y: 2, width: 22, height: 14 },
        plot: {
          chartType: 'grouped-bar',
          secondaryDimension: 'role',
          groupBySegment: false,
          defaultMetricOptions: ['postings_active'],
          plotParameters: [companyParam],
        },
      },
      {
        id: '04f3baa1-69a7-4d73-a2ec-47e905d2c94e',
        title: 'Postings Volume',
        position: { y: 17, height: 11 },
        plot: {
          chartType: 'volume',
          defaultMetricOptions: ['postings_volume'],
          plotParameters: [companyParam],
        },
      },
    ],
    [
      {
        id: 'fc8f28e2-dfca-42e3-b908-e81ed5b1442f',
        title: '',
        position: { height: 6 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Geographic Footprint\n\n{{PARAM "Company"}} has a large Southern Asia presence with significant North America operations, with smaller footprints in Western Europe, Northern Europe, Southern Europe, and Sub-Saharan Africa',
          plotParameters: companyAndCompetitorsGrouped,
        },
      },
      {
        id: '7a8402c7-3f92-47c0-b222-72ba1cebc6cd',
        title: 'Headcount',
        position: { y: 6, height: 14 },
        plot: {
          chartType: 'map',
          defaultMetricOptions: ['headcount'],
          plotParameters: [companyParam],
        },
      },
      {
        id: '1a335b7b-1aed-4c17-b524-b56a603bf4cf',
        title: 'Total Spend (Salary) by Geography',
        position: { y: 20, height: 12 },
        plot: {
          chartType: 'treemap',
          secondaryDimension: 'geography',
          defaultMetricOptions: ['total_spend_salary'],
          plotParameters: [companyParam],
        },
      },
    ],
    [
      {
        id: '7ec46b1a-bc67-4db6-a416-da38fa9d3489',
        title: '',
        position: { height: 11 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Skills, Education, and Demographics\n\n{{PARAM "Company"}} has a balanced mix of Pharmaceutical Skills, Biomedical Research, Public Health Strategy, Business Process, Data Analytics, Data Management, and Integrated Marketing capabilities. It is comprised of predominantly Bachelor degree holders (~60-70%), with substantial Master degree population\n\nGender distribution is relatively consistent compared to peers, with 60% male and 40% female representation. {{PARAM "Company"}} shows balanced White and\nAsian representation, consistent with its stated\ngeographic distribution.',
          plotParameters: [companyParam],
        },
      },
      {
        id: '208b2ef1-946a-4afd-a991-925e9b7a21b7',
        title: 'Headcount by Skills',
        position: { y: 11, width: 24, height: 9 },
        plot: {
          chartType: 'treemap',
          secondaryDimension: 'skills',
          defaultMetricOptions: ['headcount'],
          plotParameters: [companyParam],
        },
      },
      {
        id: 'e3d61c6d-4ff6-4e57-9046-8fb8c363e7fd',
        title: 'Headcount by Education',
        position: { x: 24, y: 11, width: 24, height: 9 },
        plot: {
          chartType: 'treemap',
          secondaryDimension: 'education',
          defaultMetricOptions: ['headcount'],
          plotParameters: [companyParam],
        },
      },
      {
        id: 'b5f462b0-8b20-416f-aa38-dcfd5b4f73eb',
        title: 'Headcount by Gender',
        position: { y: 20, width: 24, height: 9 },
        plot: {
          chartType: 'treemap',
          secondaryDimension: 'gender',
          defaultMetricOptions: ['headcount'],
          plotParameters: [companyParam],
        },
      },
      {
        id: '3dd8f1e6-da82-473d-b206-92905e287c52',
        title: 'Headcount by Ethnicity',
        position: { x: 24, y: 20, width: 24, height: 9 },
        plot: {
          chartType: 'treemap',
          secondaryDimension: 'ethnicity',
          defaultMetricOptions: ['headcount'],
          plotParameters: [companyParam],
        },
      },
    ],
  ]);

  return {
    id: 'report-org-health',
    name: 'Org Health',
    type: PageType.REPORT,
    owner: {
      id: 'revelio-labs',
      username: 'revelio-labs',
    },
    sampleFilterGroups: orgHealthSampleFilterGroups,
    tabs: [
      {
        label: 'Default',
        id: 'default',
        isActive: true,
        tabContent: {
          id: 'org-health-content',
          title: 'Org Health',
          description:
            'Workforce intelligence analysis revealing operational execution, productivity enhancement, employee sentiment, compensation strategy, and geographic footprint',
          grid,
          items,
          primaryFilter: {
            id: FilterId.COMPANY,
            selectionLimit: 1,
            selectionDefault: 1,
          },
          secondaryFilters: [],
          reportParameters: [
            {
              parameterId: '36eca886-b4ae-477d-8f8d-371106af8556',
              filterId: FilterId.COMPANY,
              isPrimary: true,
              name: 'Company',
            },
            {
              parameterId: 'a7ba0b5e-dcd5-45d4-a83c-9aa764d74883',
              filterId: FilterId.COMPANY,
              isPrimary: false,
              name: 'Competitors',
              selectionLimit: 9,
            },
          ],
        },
      },
    ],
  };
})();
